import { useCallback, useId, useMemo, useState } from "react";
import { findNode } from "../../Tree/utils/adapter";
import type { TreeSelectProps } from "../types";
import { resolveFieldNames } from "../utils/resolveFieldNames";

export function useTreeSelectValue(props: TreeSelectProps) {
  const {
    value: valueProp,
    defaultValue,
    onChange,
    treeData: rawTreeData,
    fieldNames,
    isClearable = false,
    isDisabled = false,
    size = "medium",
    label = "",
    error = "",
    helpText = "",
    required = false,
  } = props;

  const id = useId();
  const errorId = `error_${id}`;
  const helpTextId = `helpText_${id}`;

  const treeData = useMemo(() => {
    if (!fieldNames) return rawTreeData;

    return resolveFieldNames(rawTreeData, fieldNames);
  }, [rawTreeData, fieldNames?.label, fieldNames?.value]);

  const isControlled = valueProp !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue ?? "");
  const currentValue = isControlled ? (valueProp ?? "") : internalValue;

  const applyValue = useCallback(
    (next: string) => {
      if (!isControlled) setInternalValue(next);
      onChange?.(next);
    },
    [isControlled, onChange]
  );

  const selectedLabel = useMemo(() => {
    if (!currentValue) return "";
    const node = findNode(currentValue, treeData);
    if (!node) return currentValue;

    return typeof node.title === "string" ? node.title : "";
  }, [currentValue, treeData]);

  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      applyValue("");
    },
    [applyValue]
  );

  const showClearButton = isClearable && !!currentValue && !isDisabled;

  return {
    id,
    errorId,
    helpTextId,
    label,
    error,
    helpText,
    required,
    isDisabled,
    size,
    treeData,
    currentValue,
    selectedLabel,
    applyValue,
    handleClear,
    showClearButton,
  };
}
